import { useState, useEffect } from 'react'
import { formatVersion } from '../utils/version'
import { Play, Pause, CheckCircle, Plus } from 'lucide-react'

interface Strategy {
  id: number
  version_id: number
  strategy_type: 'percentage' | 'whitelist' | 'time_window' | 'combined'
  status: 'active' | 'paused' | 'completed'
  config: any
  created_at: string
  version_info?: VersionDetail  // 关联的版本详情
}

interface Version {
  id: number
  version: number
  file_name: string
  status: 'pending' | 'gray' | 'full' | 'rolled_back'
  created_at: string
}

interface VersionDetail {
  id: number
  version: number
  file_name: string
  file_size: number
  file_hash: string
  status: 'pending' | 'gray' | 'full' | 'rolled_back'
  release_notes?: string
  created_at: string
}

export default function GrayRelease() {
  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [pendingVersions, setPendingVersions] = useState<Version[]>([])
  const [editingStrategy, setEditingStrategy] = useState<Strategy | null>(null)
  const [editPercentage, setEditPercentage] = useState(10)
  const [editing, setEditing] = useState(false)
  const [editError, setEditError] = useState('')
  
  // 创建表单状态
  const [formData, setFormData] = useState({
    version_id: '',
    strategy_type: 'percentage' as 'percentage' | 'whitelist',
    percentage: 10,
    whitelist: '',
    description: ''
  })

  useEffect(() => {
    loadStrategies()
    loadPendingVersions()
  }, [])

  const loadStrategies = async () => {
    try {
      const appId = getAppId()
      const response = await fetch(`/api/admin/apps/${appId}/gray-release`)
      const data = await response.json()
      const strategies = data.strategies || []
      
      // 为每个策略加载关联的版本详情
      const strategiesWithVersion = await Promise.all(
        strategies.map(async (strategy: Strategy) => {
          try {
            const versionRes = await fetch(`/api/admin/versions/${strategy.version_id}`)
            if (versionRes.ok) {
              const versionData = await versionRes.json()
              return { ...strategy, version_info: versionData }
            }
          } catch (error) {
            console.error(`加载版本 ${strategy.version_id} 详情失败:`, error)
          }
          return strategy
        })
      )
      
      setStrategies(strategiesWithVersion)
    } catch (error) {
      console.error('加载策略失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadPendingVersions = async () => {
    try {
      const appId = getAppId()
      console.log('加载待发布版本，appId:', appId)
      const response = await fetch(`/api/admin/apps/${appId}/versions?status=pending`)
      const data = await response.json()
      console.log('待发布版本数据:', data)
      setPendingVersions(data.versions || [])
      // 如果有待发布版本且未选择，默认选第一个
      if (data.versions?.length > 0 && !formData.version_id) {
        setFormData(prev => ({ ...prev, version_id: data.versions[0].version.toString() }))
      }
    } catch (error) {
      console.error('加载版本列表失败:', error)
    }
  }

  const getAppId = () => {
    const pathParts = window.location.pathname.split('/')
    return pathParts[2] || 'smart_clock'
  }

  const handlePause = async (id: number) => {
    try {
      const response = await fetch(`/api/admin/gray-release/${id}/pause`, {
        method: 'POST'
      })
      if (response.ok) {
        await loadStrategies()
      }
    } catch (error) {
      console.error('暂停失败:', error)
    }
  }

  const handleResume = async (id: number) => {
    try {
      const response = await fetch(`/api/admin/gray-release/${id}/resume`, {
        method: 'POST'
      })
      if (response.ok) {
        await loadStrategies()
      }
    } catch (error) {
      console.error('恢复失败:', error)
    }
  }

  const handleComplete = async (id: number) => {
    if (!confirm('确定要完成灰度发布吗？这将把版本设置为全量发布。')) return

    try {
      const response = await fetch(`/api/admin/gray-release/${id}/complete`, {
        method: 'POST'
      })
      if (response.ok) {
        await loadStrategies()
      }
    } catch (error) {
      console.error('完成失败:', error)
    }
  }

  const handleCancel = async (id: number) => {
    if (!confirm('⚠️ 警告：确定要撤回此灰度发布吗？\n\n这将：\n1. 策略状态标记为"已取消"\n2. 版本状态标记为"已回滚"\n3. 客户端将无法再获取此版本\n\n此操作不可逆！')) return

    try {
      const response = await fetch(`/api/admin/gray-release/${id}/cancel`, {
        method: 'PUT'
      })
      if (response.ok) {
        await loadStrategies()
        await loadPendingVersions()
      }
    } catch (error) {
      console.error('撤回失败:', error)
    }
  }

  const handleEditPercentage = (strategy: Strategy) => {
    setEditingStrategy(strategy)
    setEditPercentage(strategy.config?.percentage || 10)
    setEditError('')
    setShowEditModal(true)
  }

  const handleSavePercentage = async () => {
    if (!editingStrategy) return
    
    // 验证百分比范围
    if (editPercentage < 1 || editPercentage > 100) {
      setEditError('灰度百分比必须在 1-100 之间')
      return
    }
    
    setEditing(true)
    setEditError('')
    
    try {
      const response = await fetch(`/api/admin/gray-release/${editingStrategy.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          config: {
            ...editingStrategy.config,
            percentage: Number(editPercentage)
          }
        })
      })
      
      const data = await response.json()
      
      if (data.id || data.message) {
        await loadStrategies()
        setShowEditModal(false)
        setEditingStrategy(null)
      } else {
        setEditError(data.error || data.message || '更新失败')
      }
    } catch (error: any) {
      console.error('更新失败:', error)
      setEditError(error.message || '网络错误')
    } finally {
      setEditing(false)
    }
  }

  const getStrategyTypeLabel = (type: string) => {
    switch (type) {
      case 'percentage': return '百分比放量'
      case 'whitelist': return '白名单'
      case 'time_window': return '时间窗口'
      case 'combined': return '组合策略'
      default: return type
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="text-green-400"><Play size={14} className="inline" /> 进行中</span>
      case 'paused':
        return <span className="text-yellow-400"><Pause size={14} className="inline" /> 已暂停</span>
      case 'completed':
        return <span className="text-blue-400"><CheckCircle size={14} className="inline" /> 已完成</span>
      default:
        return <span className="text-gray-400">未知</span>
    }
  }

  const handleCreate = async () => {
    setCreating(true)
    setCreateError('')
    
    // 验证百分比范围
    if (formData.strategy_type === 'percentage' && (formData.percentage < 1 || formData.percentage > 100)) {
      setCreateError('灰度百分比必须在 1-100 之间')
      setCreating(false)
      return
    }
    
    try {
      // 获取当前 appId（从 URL 或 localStorage）
      const pathParts = window.location.pathname.split('/')
      const appId = pathParts[2] || 'smart_clock'
      
      const config: any = {
        type: formData.strategy_type,
        description: formData.description
      }
      
      if (formData.strategy_type === 'percentage') {
        config.percentage = Number(formData.percentage)
      } else if (formData.strategy_type === 'whitelist') {
        config.whitelist = formData.whitelist.split(',').map((id: string) => id.trim()).filter(Boolean)
      }
      
      const response = await fetch(`/api/admin/apps/${appId}/gray-release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version_id: parseInt(formData.version_id),
          strategy_type: formData.strategy_type,
          config
        })
      })
      
      const data = await response.json()
      
      if (data.success || data.id) {
        await loadStrategies()
        await loadPendingVersions()
        setShowCreateModal(false)
        setFormData({
          version_id: '',
          strategy_type: 'percentage',
          percentage: 10,
          whitelist: '',
          description: ''
        })
      } else {
        setCreateError(data.error || data.message || '创建失败')
      }
    } catch (error: any) {
      console.error('创建策略失败:', error)
      setCreateError(error.message || '网络错误')
    } finally {
      setCreating(false)
    }
  }

  if (loading) {
    return <div className="text-center py-12 text-gray-400">加载中...</div>
  }

  return (
    <div className="space-y-6">
      {/* 头部操作 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-white">灰度发布策略</h2>
          <p className="text-sm text-gray-400 mt-1">管理版本灰度发布策略</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={16} />
          创建策略
        </button>
      </div>

      {/* 策略列表 */}
      <div className="grid gap-4">
        {strategies.map((strategy) => (
          <div key={strategy.id} className="card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-white">
                    {getStrategyTypeLabel(strategy.strategy_type)}
                  </h3>
                  {strategy.version_info && (
                    <span className="text-xs px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded">
                      {formatVersion(strategy.version_info.version)}
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  {strategy.version_info ? (
                    <>
                      <p className="text-sm text-gray-400">
                        📦 {strategy.version_info.file_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        大小：{(strategy.version_info.file_size / 1024 / 1024).toFixed(2)} MB · 
                        版本 ID: {strategy.version_info.id} · 
                        状态：{strategy.version_info.status}
                      </p>
                      <p className="text-xs text-gray-500">
                        创建于 {new Date(strategy.version_info.created_at).toLocaleString('zh-CN')}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">
                      版本 ID: {strategy.version_id} · 创建于 {new Date(strategy.created_at).toLocaleString('zh-CN')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {strategy.strategy_type === 'percentage' && strategy.status !== 'completed' && (
                  <button
                    onClick={() => handleEditPercentage(strategy)}
                    className="btn-secondary flex items-center gap-2 text-sm py-1 px-3"
                  >
                    ⚙️ 调整比例
                  </button>
                )}
                {strategy.strategy_type === 'percentage' && strategy.status === 'completed' && (
                  strategy.version_info?.status === 'rolled_back' ? (
                    <span className="text-xs text-red-400 px-3 py-1 bg-red-500/10 border border-red-500/30 rounded">
                      ⚠️ 已撤回
                    </span>
                  ) : (
                    <span className="text-xs text-green-400 px-3 py-1 bg-green-500/10 border border-green-500/30 rounded">
                      ✅ 已全量发布
                    </span>
                  )
                )}
                {getStatusBadge(strategy.status)}
                {strategy.status !== 'completed' && (
                  <button
                    onClick={() => handleCancel(strategy.id)}
                    className="btn-secondary flex items-center gap-2 text-red-400 hover:text-red-300 border-red-500/30 hover:border-red-500/50"
                    title="撤回灰度发布（固件有问题时使用）"
                  >
                    ⚠️ 撤回
                  </button>
                )}
                {strategy.status === 'active' && (
                  <button
                    onClick={() => handlePause(strategy.id)}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Pause size={16} />
                    暂停
                  </button>
                )}
                {strategy.status === 'paused' && (
                  <button
                    onClick={() => handleResume(strategy.id)}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Play size={16} />
                    恢复
                  </button>
                )}
                {strategy.status === 'active' && (
                  <button
                    onClick={() => handleComplete(strategy.id)}
                    className="btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle size={16} />
                    完成发布
                  </button>
                )}
              </div>
            </div>

            {/* 策略配置 */}
            <div className="bg-dark-border rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-300 mb-2">策略配置</h4>
              {strategy.strategy_type === 'percentage' ? (
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-400">灰度比例</span>
                      <span className="text-sm font-semibold text-cyan-400">{strategy.config?.percentage || 0}%</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-300"
                        style={{ width: `${strategy.config?.percentage || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              ) : strategy.strategy_type === 'whitelist' ? (
                <div className="text-xs text-gray-400">
                  白名单设备：{strategy.config?.whitelist?.length || 0} 台
                </div>
              ) : (
                <pre className="text-xs text-gray-400 font-mono">
                  {JSON.stringify(strategy.config, null, 2)}
                </pre>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 创建策略弹窗 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-2xl">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-white">创建灰度发布策略</h3>
                <p className="text-sm text-gray-400 mt-1">配置版本的灰度发布策略</p>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm text-gray-400 mb-2">版本号 *</label>
                <select
                  value={formData.version_id}
                  onChange={(e) => setFormData({ ...formData, version_id: e.target.value })}
                  className="input w-full"
                >
                  <option value="">请选择版本</option>
                  {pendingVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {formatVersion(v.version)} - {v.file_name}
                    </option>
                  ))}
                </select>
                {pendingVersions.length === 0 && (
                  <p className="text-xs text-yellow-500 mt-1">
                    ⚠️ 暂无待发布版本，请先上传固件
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">策略类型 *</label>
                <select
                  value={formData.strategy_type}
                  onChange={(e) => setFormData({ ...formData, strategy_type: e.target.value as 'percentage' | 'whitelist' })}
                  className="input w-full"
                >
                  <option value="percentage">百分比放量</option>
                  <option value="whitelist">白名单</option>
                </select>
              </div>

              {formData.strategy_type === 'percentage' ? (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">灰度百分比 (%)</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.percentage}
                    onChange={(e) => setFormData({ ...formData, percentage: Number(e.target.value) })}
                    className="input w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">初始灰度百分比，后续可手动调整</p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">白名单设备 ID</label>
                  <textarea
                    value={formData.whitelist}
                    onChange={(e) => setFormData({ ...formData, whitelist: e.target.value })}
                    placeholder="AA:BB:CC:11:22:33,DD:EE:FF:44:55:66"
                    className="input w-full"
                    rows={4}
                  />
                  <p className="text-xs text-gray-500 mt-1">多个设备 ID 用英文逗号分隔</p>
                </div>
              )}

              <div>
                <label className="block text-sm text-gray-400 mb-2">策略描述</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="描述此灰度发布策略的目的和注意事项..."
                  className="input w-full"
                  rows={3}
                />
              </div>

              {createError && (
                <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-3 rounded-lg text-sm">
                  {createError}
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn-secondary"
                disabled={creating}
              >
                取消
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !formData.version_id}
                className="btn-primary"
              >
                {creating ? '创建中...' : '创建策略'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 编辑比例弹窗 */}
      {showEditModal && editingStrategy && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-md">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-white">调整灰度比例</h3>
                <p className="text-sm text-gray-400 mt-1">
                  版本 {formatVersion(editingStrategy.version_id)} - 当前比例：{editingStrategy.config?.percentage || 10}%
                </p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm text-gray-400 mb-2">灰度百分比 (%)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={editPercentage}
                  onChange={(e) => setEditPercentage(Number(e.target.value))}
                  className="input w-full"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  调整后将立即影响新请求升级的设备
                </p>
              </div>

              {editError && (
                <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-3 rounded-lg text-sm">
                  {editError}
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn-secondary"
                disabled={editing}
              >
                取消
              </button>
              <button
                onClick={handleSavePercentage}
                disabled={editing}
                className="btn-primary"
              >
                {editing ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
